import type { ECS, TaskStopCode } from '@aws-sdk/client-ecs';
import type { AWSRegion } from '../backends/lambda/AWSRegion';
import SB from '../SchemaBuilder';
import { duration } from '../utils/Utils';

/** Task-specific argument object schemas */
export const taskSchemas = {
    archiveInactiveUsers: SB.object({
        count: SB.number().default(1).min(1).max(25),
        concurrency: SB.number().default(2).min(1).max(100),

        /** If `true`, do not write any changes to DB or S3. */
        dryRun: SB.boolean().default(false),

        where: SB.array(SB.map(SB.unknown())),
    }),
    chatbotBroadcast: SB.object({
        count: SB.number().default(1).min(1).max(25),
        concurrency: SB.number().default(10).min(2).max(100),
        forceChannel: SB.tuple(['android', 'chatbot', 'ios', 'nativepush', 'sms', 'telegram', 'webpush']).optional(),
        messageTemplateArgs: SB.unknown(),
        messageTemplateName: SB.string(),
        payload: SB.string()
            .default(JSON.stringify({ feature: 'broadcast', $channel: 'CHATBOT' }))
            .customValidator((val) => {
                const parsedVal = JSON.parse(val);
                return parsedVal && typeof parsedVal === 'object' ? null : 'Enter a JSON-encoded object';
            }),
        where: SB.array(SB.map(SB.unknown())),
    }),
    populateMessengerIdMapping: SB.object({
        /** Number of ECS task instances */
        count: SB.number().default(1).min(1).max(25),
        /** Number of workers per task instance */
        concurrency: SB.number().default(2).min(1).max(100),
        easing: SB.number()
            .default(duration({ minutes: 10 }))
            .min(0),
        /** Max number of ES reads per second */
        esReadCapacity: SB.number().default(1000).min(1).max(50_000),
    }),
    reindex: SB.object({
        count: SB.number().default(1).min(1).max(25),
        concurrency: SB.number().default(1).min(1).max(100),
        easing: SB.number()
            .default(duration({ minutes: 10 }))
            .min(0),
        esPushCapacity: SB.number().default(1000).min(1).max(50_000),
        dbReadCapacity: SB.number().default(1000).min(1).max(500_000),
    }),
    reindexIndexed: SB.object({
        count: SB.number().default(1).min(1).max(25),
        concurrency: SB.number().default(2).min(2).max(100),
        easing: SB.number()
            .default(duration({ minutes: 10 }))
            .min(0),
        esPushCapacity: SB.number().default(1000).min(1).max(50_000),
        dbReadCapacity: SB.number().default(1000).min(1).max(500_000),
        sharedStateIndex: SB.string().optional(),
    }),
    scheduleActionToMany: SB.object({
        /** Scheduled action arguments. */
        actionArgs: SB.unknown(),

        /** Scheduled action name. */
        actionName: SB.string(),

        /** Number of concurrent workers per task instance. */
        concurrency: SB.number().default(2).min(2).max(100),

        /** Number of ECS task instances. */
        count: SB.number().default(1).min(1).max(25),

        /** Scheduled action delay in milliseconds. */
        delayMs: SB.int()
            .min(duration({ minutes: 1 }))
            .optional(),

        /** Scheduled action notification ID. */
        notificationId: SB.string().customValidator((val) =>
            val.length === 0 ? 'notificationId length must be >0' : null,
        ),

        /** Path to an S3 object that consists of whitespace separated user IDs. Define either `userIdsS3Path` or `where` to select action receivers. */
        userIdsS3Path: SB.string().optional(),

        /** Index query used to select scheduled action receivers. Define either `userIdsS3Path` or `where` to select action receivers. */
        where: SB.array(SB.map(SB.unknown())).optional(),
    }),
};

export const taskTypes = Object.keys(taskSchemas) as TaskType[];
export type TaskType = keyof typeof taskSchemas;
export type TaskArgs<T extends TaskType> = SB.ExtractType<(typeof taskSchemas)[T]>;

/** Shared task context argument object schema */
export const contextSchema = SB.object({
    now: SB.int(),
    taskId: SB.string(),
    taskIndex: SB.int(),
    taskType: SB.tuple<TaskType>(taskTypes as any), // TODO handle ('a' | 'b')[] in SB.union to avoid coercion
});

type ECSTaskStatus =
    | 'PROVISIONING'
    | 'PENDING'
    | 'ACTIVATING'
    | 'RUNNING'
    | 'DEACTIVATING'
    | 'STOPPING'
    | 'DEPROVISIONING'
    | 'STOPPED';

type ECSTask = {
    status: ECSTaskStatus;
    stoppedAt?: number;
    stopCode?: TaskStopCode;
};

export type ECSTasksMap = { [id: string]: ECSTask };

export const taskRunnerConfig = {
    buildsBucketName: 'taskrunner-builds',
    taskDefinitionFamily: 'taskrunner',
    clusterName: 'taskrunner-cluster',
    containerName: 'image',
    region: 'us-east-1' as AWSRegion,
    subnets: ['subnet-053dddef1c1908ccc', 'subnet-01f73e6c02ce491af'],
};

export type TaskRunnerClient = {
    getTasks: (ecsTaskIds: string[]) => Promise<ECSTasksMap>;
    runTask: <T extends TaskType>(opts: { args: TaskArgs<T>; id: string; task: T }) => Promise<ECSTasksMap>;
    stopTask: (ecsTaskId: string) => Promise<void>;
};

export function createTaskRunnerClient(
    ecs: ECS,
    appOpts: { appName: string; appVersion?: string; stage: string },
): TaskRunnerClient {
    const { appName, appVersion, stage } = appOpts;

    return {
        getTasks: async (ecsTaskIds) => {
            // TODO handle ecsTaskIds.length > 100
            const { tasks } = await ecs.describeTasks({ tasks: ecsTaskIds, cluster: taskRunnerConfig.clusterName });

            const result: { [id: string]: ECSTask } = {};
            for (const task of tasks ?? []) {
                result[arnToId(task.taskArn!)] = {
                    status: task.lastStatus as ECSTaskStatus,
                    stoppedAt: (task.stoppedAt ?? task.executionStoppedAt)?.getTime(),
                    stopCode: task.stopCode,
                };
            }

            return result;
        },

        runTask: async (opts) => {
            // Resolve S3 paths of source and dependencies bundles, as deployed by `tasks deploy` CLI:
            const archiveName = `tasks-${appName}-v${appVersion}.zip`;
            const sourceBundleKey = `${appName}/${stage}/${archiveName}`;

            // Run ECS Tasks:
            const taskPromises = new Array(opts.args.count || 1).fill(0).map(async (_, i) => {
                const contextArgs: SB.ExtractType<typeof contextSchema> = {
                    now: Date.now(),
                    taskId: opts.id,
                    taskIndex: i,
                    taskType: opts.task,
                };

                const ecsTask = await ecs.runTask({
                    taskDefinition: taskRunnerConfig.taskDefinitionFamily,
                    cluster: taskRunnerConfig.clusterName,
                    group: opts.task,
                    launchType: 'FARGATE',
                    networkConfiguration: {
                        awsvpcConfiguration: {
                            assignPublicIp: 'ENABLED',
                            subnets: taskRunnerConfig.subnets,
                        },
                    },
                    overrides: {
                        containerOverrides: [
                            {
                                name: taskRunnerConfig.containerName,
                                environment: [
                                    { name: 'CONTEXT_ARGS', value: JSON.stringify(contextArgs) },
                                    { name: 'TASK_ARGS', value: JSON.stringify(opts.args) },

                                    // S3_SOURCE_KEY is read by the Docker startup shell script, hence it's
                                    // not packed inside the CONTEXT_ARGS object:
                                    { name: 'S3_SOURCE_KEY', value: sourceBundleKey },
                                ],
                            },
                        ],
                    },
                });

                if (ecsTask.tasks?.length === 1) {
                    const id = arnToId(ecsTask.tasks[0]!.taskArn!);
                    const task: ECSTask = { status: ecsTask.tasks[0]!.lastStatus as ECSTaskStatus };

                    return { [id]: task };
                } else {
                    // TODO stop all tasks if one failed
                    throw new Error('Failed to run task: ' + JSON.stringify(ecsTask.failures));
                }
            });

            return Object.assign({}, ...(await Promise.all(taskPromises)));
        },

        stopTask: async (ecsTaskId) => {
            await ecs.stopTask({ task: ecsTaskId, cluster: taskRunnerConfig.clusterName });
        },
    };
}

const arnToId = (arn: string) => arn.split('/').pop()!;
